// src/app/(marketing)/practice-exams/[slug]/PracticeExamClient.tsx
'use client'

import { useState, useCallback } from 'react'
import Link from 'next/link'
import { ChevronLeft, CheckCircle2, Grid } from 'lucide-react'
import { PracticeQuestion } from '@/components/exam/PracticeQuestion'
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet'

interface PracticeOption {
  id: string
  text: string
  optionKey: string
  isCorrect: boolean
  imageUrl: string | null
}

interface PracticeExamQuestion {
  id: string
  statement: string
  imageUrl: string | null
  explanation: string | null
  type: string
  matchPairs?: any
  correctAnswerExact?: number | null
  correctAnswerMin?: number | null
  correctAnswerMax?: number | null
  options: PracticeOption[]
}

interface PracticeExam {
  id: string
  title: string
  slug: string
  description: string | null
  questionCount: number
  subject: { id: string; name: string; slug: string }
  questions: PracticeExamQuestion[]
}

interface Props {
  exam: PracticeExam
  backTo?: string
}

export function PracticeExamClient({ exam, backTo }: Props) {
  // Track how many questions have been answered (checked)
  const [answeredCount, setAnsweredCount] = useState(0)
  const [questionStates, setQuestionStates] = useState<Record<string, 'correct' | 'incorrect'>>({})

  const handleAnswered = useCallback((questionId: string, isCorrect: boolean) => {
    setQuestionStates(prev => ({
      ...prev,
      [questionId]: isCorrect ? 'correct' : 'incorrect'
    }))
    setAnsweredCount(prev => prev + 1)
  }, [])

  const scrollToQuestion = (id: string) => {
    const el = document.getElementById(`question-${id}`)
    if (el) {
      // Offset for sticky header (around 70px + some padding)
      const y = el.getBoundingClientRect().top + window.scrollY - 100
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  const renderPalette = () => (
    <div className="grid grid-cols-5 gap-2">
      {exam.questions.map((q, i) => {
        const state = questionStates[q.id]
        let btnClass = "bg-white border-gray-200 text-gray-700 hover:border-blue-400 hover:text-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:border-blue-500"
        
        if (state === 'correct') {
          btnClass = "bg-green-500 border-green-500 text-white hover:bg-green-600"
        } else if (state === 'incorrect') {
          btnClass = "bg-red-500 border-red-500 text-white hover:bg-red-600"
        }

        return (
          <button
            key={q.id}
            onClick={() => scrollToQuestion(q.id)}
            className={`flex items-center justify-center w-10 h-10 rounded-lg border text-sm font-semibold transition-all ${btnClass}`}
          >
            {i + 1}
          </button>
        )
      })}
    </div>
  )

  const totalQuestions = exam.questions.length
  const allAnswered = answeredCount === totalQuestions

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-16">

      {/* ── Sticky progress bar ─────────────────────────────────────── */}
      <div className="sticky top-0 z-40 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          {/* Back link */}
          <Link
            href={backTo || "/practice-exams"}
            className="flex items-center gap-2 text-base font-semibold text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Exams</span>
            <span className="sm:hidden">Back</span>
          </Link>

          {/* Progress indicator */}
          <div className="flex items-center gap-4 min-w-0">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
              {answeredCount} of {totalQuestions} answered
            </span>
            {/* Progress pill */}
            <div className="hidden sm:block w-40 h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-blue-500 to-violet-500 transition-all duration-500"
                style={{ width: `${totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0}%` }}
              />
            </div>

            {/* Mobile Palette Trigger */}
            <div className="lg:hidden ml-2">
              <Sheet>
                <SheetTrigger asChild>
                  <button className="flex items-center justify-center w-9 h-9 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:text-blue-600 transition-colors">
                    <Grid className="w-5 h-5" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[350px] overflow-y-auto">
                  <SheetHeader className="mb-6 text-left">
                    <SheetTitle>Question Palette</SheetTitle>
                  </SheetHeader>
                  <div className="flex gap-4 text-xs font-medium text-gray-500 mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" /> Unattempted</div>
                    <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-green-500" /> Correct</div>
                    <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-500" /> Incorrect</div>
                  </div>
                  {renderPalette()}
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-2">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
            {exam.subject.name}
          </span>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white max-w-3xl">
            {exam.title}
          </h1>
          {exam.description && (
            <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed max-w-3xl">
              {exam.description}
            </p>
          )}
          <p className="text-xs text-gray-400 dark:text-gray-500 pt-1">
            {totalQuestions} question{totalQuestions !== 1 ? 's' : ''} · Click an option and press{' '}
            <span className="font-medium text-gray-600 dark:text-gray-300">Check Answer</span> to reveal the result
          </p>
        </div>
      </div>

      {/* ── Main Layout ─────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8 items-start">
          
          {/* ── Left Column: Questions ── */}
          <div className="flex-1 w-full max-w-3xl space-y-6 lg:max-w-none">
            {exam.questions.map((question, index) => (
              <PracticeQuestion
                key={question.id}
                question={question}
                questionNumber={index + 1}
                onAnswered={(isCorrect) => handleAnswered(question.id, isCorrect)}
              />
            ))}

            {/* ── Completion banner — shown once all answered ─────────── */}
            {allAnswered && totalQuestions > 0 && (
              <div className="flex flex-col items-center gap-4 py-10 text-center bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-950/40">
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    All done!
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    You answered all {totalQuestions} questions. Great work!
                  </p>
                </div>
                <Link
                  href="/practice-exams"
                  className="mt-2 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                >
                  Try Another Practice Exam
                </Link>
              </div>
            )}
          </div>

          {/* ── Right Column: Desktop Palette ── */}
          <div className="hidden lg:block w-[320px] shrink-0 sticky top-28 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-6 shadow-sm">
            <h3 className="font-bold text-gray-900 dark:text-white mb-4">Question Palette</h3>
            
            <div className="flex gap-4 text-[11px] font-medium text-gray-500 mb-6 pb-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" /> Unattempted</div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-green-500" /> Correct</div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm bg-red-500" /> Incorrect</div>
            </div>

            <div className="max-h-[calc(100vh-250px)] overflow-y-auto pr-2 custom-scrollbar">
              {renderPalette()}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}